import { createElement } from "react";
import { createRoot } from "react-dom/client";
import { App } from "./App";
import { store } from "./store";
import { disableGlobalFocusTrapForPyneUI } from "./lib/disableGlobalFocusTrapForPyneUI";
import { installSegmentIntegration } from "./installSegmentIntegration";
import { loadCustomPyneSettings } from "./loadCustomPyneSettings";
import { TrackEvents } from "@common/schemas/track";

const pyne = createGlobalPyneInstance();
export default pyne;

whenReady(() => {
  startPyne();
});

function startPyne() {
  const pyneSettings = loadCustomPyneSettings(window.pyneSettings ?? {});
  const browserDetection = pyneSettings.browserDetection !== false;
  const isMobile = window.matchMedia("only screen and (max-width: 760px)").matches;
  if (browserDetection && isMobile) {
    console.log("Pyne: Not loading on mobile");
    return;
  }

  store.setPyneSettings(pyneSettings);
  store.init();
  void store.pyneClient.runQueuedCommands();

  if (pyneSettings.segment) {
    installSegmentIntegration();
  }

  if (pyneSettings.theme) {
    document.body.setAttribute("data-pyne-theme", pyneSettings.theme);
    if (isAvatarPositionRight()) {
      store.setAvatarInitialPositionRight();
    }
  }

  if (!store.isPreview && !pyneSettings.skipTracking) {
    window.pyne("track", "pyne_loaded", {} satisfies Partial<TrackEvents.PyneLoaded>);
  }

  initializeUi();
}

function createGlobalPyneInstance(): unknown {
  // Add loader function to window if it doesn't exist. This happends when the script is loaded
  // directly during development or when used as a module.
  if (typeof window.pyne !== "function") {
    const e: any = function () {
      e.c(arguments);
    };
    e.q = [];
    e.c = function (t: any) {
      e.q.push(t);
    };
    window.pyne = e;
  }

  const pyne: any = window.pyne;

  if ("store" in pyne) {
    return pyne;
  }

  pyne.store = store.pyneClient;
  pyne.VERSION = process.env.VERSION;
  pyne.enableDebug = () => store.enableDebug();
  pyne.disableDebug = () => store.disableDebug();
  return pyne;
}

function initializeUi() {
  if (window.self !== window.top) {
    // Don't initialize UI if inside an iframe
    return;
  }

  disableGlobalFocusTrapForPyneUI();

  let container = document.getElementById("pyne-widget");
  if (!container) {
    container = document.createElement("div");
    container.id = "pyne-widget";
    document.body.appendChild(container);
  }
  const root = createRoot(container!);
  root.render(createElement(App));
}

function whenReady(fn: () => void) {
  // see if DOM is already available
  if (document.readyState === "complete" || document.readyState === "interactive") {
    // call on next available tick
    setTimeout(fn, 1);
  } else {
    document.addEventListener("DOMContentLoaded", fn);
  }
}

function isAvatarPositionRight() {
  return getComputedStyle(document.body).getPropertyValue("--pyne-avatar-position-right") === "1";
}
